import { useEffect, useMemo, useRef, useState } from 'react';
import { orderBy } from 'firebase/firestore';
import {
  TrainingInstructionsSchema,
  type Job,
  type TrainingSession,
  type TrainingSource,
  type TrainingState,
} from '@allemnii-pulse/shared';
import { useCollection, useDocument } from '../../lib/firestore.js';
import { api, ApiError } from '../../lib/api.js';

/** Live reads of a training project — see services/training.ts (API) for the layout. */
export function useTrainingState(projectId: string) {
  const live = useDocument<TrainingState>(`projects/${projectId}/training/state`);
  // The doc is read unparsed; its instructions are parsed so fields added since it was written
  // (density, the design's style) have their defaults.
  const data = useMemo(() => {
    if (!live.data) return live.data;
    const parsed = TrainingInstructionsSchema.safeParse(live.data.instructions);
    return parsed.success ? { ...live.data, instructions: parsed.data } : live.data;
  }, [live.data]);
  return { ...live, data };
}

const BY_ORDER = [orderBy('order')];
const BY_ADDED = [orderBy('addedAt')];

export function useTrainingSessions(projectId: string) {
  return useCollection<TrainingSession>(`projects/${projectId}/trainingSessions`, BY_ORDER);
}

export function useTrainingSources(projectId: string) {
  return useCollection<TrainingSource>(`projects/${projectId}/trainingSources`, BY_ADDED);
}

/**
 * Starts a generation job and watches its jobs/{id} doc until it finishes. `run` posts to the
 * API and returns its { jobId } (or null when the call didn't start one); onSuccess gets the job
 * result. Only one job at a time per hook.
 */
export function useJobRunner(onSuccess?: (result: Record<string, unknown> | null) => void) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: job } = useDocument<Job>(jobId ? `jobs/${jobId}` : null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    if (!job) return;
    if (job.status === 'succeeded') {
      setJobId(null);
      onSuccessRef.current?.(job.result);
    } else if (job.status === 'failed') {
      setJobId(null);
      setError(job.error ?? 'Generation failed');
    }
  }, [job]);

  async function start(run: () => Promise<{ jobId: string | null }>, fallbackError: string) {
    setStarting(true);
    setError(null);
    try {
      const { jobId: id } = await run();
      if (id) setJobId(id);
      else onSuccessRef.current?.(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : fallbackError);
    } finally {
      setStarting(false);
    }
  }

  return { start, running: starting || jobId !== null, error, setError };
}

/** POST to a training endpoint that answers with { jobId }. */
export function postJob(projectId: string, path: string, body: unknown = {}) {
  return api.post<{ jobId: string | null }>(`/projects/${projectId}/training${path}`, body);
}
